import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft,
    Check,
    Clock3,
    Heart,
    Info,
    Minus,
    Package,
    Plus,
    ShieldCheck,
    ShoppingCart,
    Truck,
} from "lucide-react";
import { toast } from "sonner";
import { useProduct } from "@/features/catalog/hooks";
import { ProductImage } from "@/components/catalog/ProductImage";
import { StarRating } from "@/components/catalog/StarRating";
import { SubmitButton } from "@/components/form/SubmitButton";
import { useCart } from "@/features/cart/CartContext";
import { useWishlist } from "@/features/wishlist/WishlistContext";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

function formatINR(value: number): string {
    return `₹${value.toLocaleString("en-IN")}`;
}

/**
 * ProductDetails — rich PDP with a 4-variant image gallery, price block,
 * quantity stepper, wishlist toggle, description, ingredients and
 * nutritional information (both optional).
 */
export default function ProductDetails() {
    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: product, isLoading, isError } = useProduct(id);
    const { getQuantity, add, setQuantity: setCartQty } = useCart();
    const { has, toggle } = useWishlist();
    const [activeVariant, setActiveVariant] = useState<0 | 1 | 2 | 3>(0);
    const [localQty, setLocalQty] = useState(1);

    if (isLoading) {
        return <LoadingSkeleton />;
    }
    if (isError || !product) {
        return <NotFoundState />;
    }

    const inCartQty = getQuantity(product.id);
    const wished = has(product.id);
    const outOfStock = !product.in_stock || product.stock_count <= 0;
    const discount = Math.max(
        0,
        Math.round(((product.mrp - product.price) / product.mrp) * 100),
    );
    const hasIngredients = product.ingredients.length > 0;
    const hasNutrition = product.nutrition.length > 0;

    const onAddToCart = async () => {
        if (outOfStock) return;
        try {
            if (inCartQty > 0) {
                await setCartQty(product.id, inCartQty + localQty);
            } else {
                await add(product.id, localQty);
            }
            toast.success("Added to cart", {
                description: `${localQty} × ${product.name}`,
            });
        } catch {
            toast.error("Couldn't add to cart", {
                description: "Please try again in a moment.",
            });
        }
    };

    const onWishlist = () => {
        const added = toggle(product.id);
        toast(added ? "Added to wishlist" : "Removed from wishlist", {
            description: product.name,
        });
    };

    return (
        <div data-testid="product-details-page" className="tsgo-page">
            <section className="tsgo-container pt-4 sm:pt-6">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground transition hover:text-brand"
                    data-testid="pdp-back"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                </button>

                <div className="mt-4 grid gap-8 md:grid-cols-2 lg:gap-12">
                    {/* Gallery */}
                    <div className="space-y-3">
                        <ProductImage
                            product={product}
                            variant={activeVariant}
                            size="hero"
                            className="aspect-square w-full overflow-hidden rounded-3xl border border-border/60"
                        />
                        <div className="grid grid-cols-4 gap-2" data-testid="pdp-gallery">
                            {[0, 1, 2, 3].map((v) => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setActiveVariant(v as 0 | 1 | 2 | 3)}
                                    aria-label={`Show image ${v + 1}`}
                                    aria-pressed={activeVariant === v}
                                    data-testid={`pdp-thumb-${v}`}
                                    className={cn(
                                        "aspect-square overflow-hidden rounded-xl border transition",
                                        activeVariant === v
                                            ? "border-brand ring-2 ring-brand/40"
                                            : "border-border hover:border-brand/50",
                                    )}
                                >
                                    <ProductImage
                                        product={product}
                                        variant={v as 0 | 1 | 2 | 3}
                                        size="thumb"
                                        className="h-full w-full"
                                    />
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Info column */}
                    <div className="flex flex-col">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                            <Link
                                to={`/categories/${product.category_slug}`}
                                className="hover:text-brand"
                                data-testid="pdp-category-link"
                            >
                                {product.category_name}
                            </Link>
                            {" · "}
                            <span data-testid="pdp-brand">{product.brand}</span>
                        </p>

                        <h1
                            className="mt-1 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl"
                            data-testid="pdp-name"
                        >
                            {product.name}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground" data-testid="pdp-weight">
                            {product.weight}
                        </p>

                        <div className="mt-3 flex flex-wrap items-center gap-3">
                            <StarRating value={product.rating} count={product.rating_count} size="md" />
                            <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand">
                                <Clock3 className="h-3.5 w-3.5" />
                                Delivered in {product.eta_minutes} min
                            </span>
                            {outOfStock ? (
                                <span
                                    className="inline-flex items-center gap-1 rounded-full bg-error/10 px-2.5 py-1 text-xs font-bold text-error"
                                    data-testid="pdp-stock-status"
                                >
                                    Out of stock
                                </span>
                            ) : product.stock_count <= 15 ? (
                                <span
                                    className="inline-flex items-center gap-1 rounded-full bg-orange-100 px-2.5 py-1 text-xs font-bold text-orange-700"
                                    data-testid="pdp-stock-status"
                                >
                                    Only {product.stock_count} left
                                </span>
                            ) : (
                                <span
                                    className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700"
                                    data-testid="pdp-stock-status"
                                >
                                    <Check className="h-3 w-3" /> In stock
                                </span>
                            )}
                        </div>

                        {/* Price */}
                        <div className="mt-6 flex items-end gap-3" data-testid="pdp-price-block">
                            <span className="text-3xl font-extrabold text-foreground">
                                {formatINR(product.price)}
                            </span>
                            {discount > 0 ? (
                                <>
                                    <span className="text-base font-medium text-muted-foreground line-through">
                                        {formatINR(product.mrp)}
                                    </span>
                                    <span className="rounded-full bg-brand/15 px-2.5 py-1 text-xs font-bold text-brand">
                                        {discount}% off
                                    </span>
                                </>
                            ) : null}
                        </div>
                        <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
                            <Info className="h-3 w-3" /> Inclusive of all taxes
                        </p>

                        {/* Qty + CTAs */}
                        <div className="mt-6 flex flex-wrap items-center gap-3">
                            <div className="inline-flex h-12 items-center overflow-hidden rounded-full border border-border/70">
                                <button
                                    type="button"
                                    onClick={() => setLocalQty((q) => Math.max(1, q - 1))}
                                    disabled={outOfStock || localQty <= 1}
                                    aria-label="Decrease"
                                    data-testid="pdp-qty-dec"
                                    className="grid h-full w-12 place-items-center text-foreground/70 transition hover:bg-muted disabled:opacity-40"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <span
                                    className="w-10 text-center text-sm font-bold"
                                    data-testid="pdp-qty-value"
                                >
                                    {localQty}
                                </span>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setLocalQty((q) => Math.min(product.stock_count, q + 1))
                                    }
                                    disabled={outOfStock || localQty >= product.stock_count}
                                    aria-label="Increase"
                                    data-testid="pdp-qty-inc"
                                    className="grid h-full w-12 place-items-center text-foreground/70 transition hover:bg-muted disabled:opacity-40"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>

                            <SubmitButton
                                type="button"
                                fullWidth={false}
                                onClick={onAddToCart}
                                disabled={outOfStock}
                                data-testid="pdp-add-to-cart"
                                className="h-12 flex-1 min-w-[180px]"
                            >
                                <ShoppingCart className="mr-2 h-4 w-4" />
                                {inCartQty > 0
                                    ? `In cart · ${inCartQty} · Add more`
                                    : "Add to cart"}
                            </SubmitButton>

                            <button
                                type="button"
                                onClick={onWishlist}
                                aria-pressed={wished}
                                aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
                                data-testid="pdp-wishlist"
                                className={cn(
                                    "grid h-12 w-12 place-items-center rounded-full border transition",
                                    wished
                                        ? "border-error bg-error text-white"
                                        : "border-border bg-background text-foreground/70 hover:border-error hover:text-error",
                                )}
                            >
                                <Heart className={cn("h-5 w-5", wished && "fill-current")} />
                            </button>
                        </div>

                        {/* Trust row */}
                        <div className="mt-6 grid gap-2 rounded-2xl border border-border/60 bg-muted/40 p-3 sm:grid-cols-3">
                            <TrustPill icon={<Truck className="h-4 w-4" />} label="Fast delivery" />
                            <TrustPill icon={<ShieldCheck className="h-4 w-4" />} label="Secure payments" />
                            <TrustPill icon={<Package className="h-4 w-4" />} label="Easy returns" />
                        </div>
                    </div>
                </div>

                {/* Description */}
                <section className="mt-10 grid gap-8 lg:grid-cols-3">
                    <article
                        className="lg:col-span-2 rounded-2xl border border-border/60 bg-card p-5 sm:p-6"
                        data-testid="pdp-description"
                    >
                        <h2 className="tsgo-h3">Product details</h2>
                        <p className="mt-3 text-sm leading-relaxed text-foreground/80">
                            {product.description}
                        </p>

                        {hasIngredients ? (
                            <div className="mt-5" data-testid="pdp-ingredients">
                                <h3 className="text-sm font-bold uppercase tracking-wide text-muted-foreground">
                                    Ingredients
                                </h3>
                                <ul className="mt-2 flex flex-wrap gap-2">
                                    {product.ingredients.map((ing) => (
                                        <li
                                            key={ing}
                                            className="rounded-full border border-border/70 bg-background px-3 py-1 text-xs font-medium text-foreground/80"
                                        >
                                            {ing}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ) : null}
                    </article>

                    {hasNutrition ? (
                        <aside
                            className="rounded-2xl border border-border/60 bg-card p-5 sm:p-6"
                            data-testid="pdp-nutrition"
                        >
                            <h2 className="tsgo-h3">Nutritional information</h2>
                            <p className="mt-1 text-xs text-muted-foreground">
                                Values per serving unless stated otherwise.
                            </p>
                            <dl className="mt-4 divide-y divide-border/70">
                                {product.nutrition.map((n) => (
                                    <div
                                        key={n.label}
                                        className="flex items-center justify-between py-2.5 text-sm"
                                    >
                                        <dt className="text-muted-foreground">
                                            {n.label}
                                        </dt>
                                        <dd className="font-semibold text-foreground">
                                            {n.value}
                                        </dd>
                                    </div>
                                ))}
                            </dl>
                        </aside>
                    ) : null}
                </section>
            </section>
        </div>
    );
}

function TrustPill({ icon, label }: { icon: React.ReactNode; label: string }) {
    return (
        <div className="flex items-center gap-2 rounded-xl bg-background px-3 py-2 text-xs font-semibold text-foreground/80">
            <span className="grid h-6 w-6 place-items-center rounded-full bg-brand/10 text-brand">
                {icon}
            </span>
            {label}
        </div>
    );
}

function LoadingSkeleton() {
    return (
        <div className="tsgo-container pt-6" data-testid="pdp-loading">
            <div className="grid gap-8 md:grid-cols-2 lg:gap-12">
                <div className="aspect-square w-full animate-pulse rounded-3xl bg-muted" />
                <div className="space-y-4">
                    <div className="h-3 w-1/3 animate-pulse rounded-full bg-muted" />
                    <div className="h-8 w-2/3 animate-pulse rounded-lg bg-muted" />
                    <div className="h-3 w-1/4 animate-pulse rounded-full bg-muted" />
                    <div className="h-4 w-1/2 animate-pulse rounded-full bg-muted" />
                    <div className="h-10 w-1/3 animate-pulse rounded-full bg-muted" />
                    <div className="h-12 w-full animate-pulse rounded-full bg-muted" />
                </div>
            </div>
        </div>
    );
}

function NotFoundState() {
    return (
        <div className="tsgo-container flex min-h-[60vh] items-center justify-center py-16">
            <div className="max-w-md rounded-2xl border border-border/70 bg-card p-8 text-center">
                <Package className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
                <h2 className="text-xl font-semibold">Product not found</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                    This product may have been removed or the link is broken.
                </p>
                <div className="mt-5">
                    <SubmitButton
                        type="button"
                        variant="outline"
                        fullWidth={false}
                        onClick={() => (window.location.href = ROUTES.categories)}
                    >
                        Browse categories
                    </SubmitButton>
                </div>
            </div>
        </div>
    );
}
